import React from 'react'
import { View, Pressable, Platform } from 'react-native'
import cn from '@ui/utils/cn'
import Typography from '@ui/typography'
import IconBadge from '@ui/elements/IconBadge'
import { IconBadgeProps } from '@ui/elements/IconBadge/types'
import { IconName } from '@ui/elements/Icon'
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import { colors } from '@ui/colors'
import { sIconContainer } from '@ui/elements/IconBadge/styles'
import Gradient from '@ui/effects/Gradient'
import { hex } from '@ui/utils/hex'
import { DetailRowSize, heightVariantMap, titleVariantMap } from './styles'
import { GradientType } from '@ui/types'

interface Props {
  title?: string
  titleComponent?: React.ReactNode
  desc?: string | React.ReactNode
  icon?: IconName
  iconProps?: Omit<IconBadgeProps, 'name'>
  iconComponent?: React.ReactNode
  onPress?: () => void
  children?: React.ReactNode
  className?: string
  classNameInner?: string
  classNameDesc?: string
  color?: string
  first?: boolean
  stronger?: boolean
  success?: boolean
  danger?: boolean
  disabled?: boolean
  size?: DetailRowSize
}

export default function DetailRow({
  title,
  titleComponent,
  desc,
  icon,
  iconComponent,
  iconProps,
  children,
  onPress,
  className,
  classNameInner,
  classNameDesc,
  color,
  first,
  stronger,
  success,
  danger,
  disabled,
  size = 'md',
}: Props) {
  const {
    onPressIn,
    onPressOut,
    animation: animationPress,
  } = useAnimatedPress({
    duration: 300,
  })

  const animationShared = useSharedValue(0)

  const animation = onPress ? animationPress : animationShared

  const stylePressed = useAnimatedStyle(() => {
    return {
      opacity: interpolate(animation.value, [0, 1], [0, 1]),
    }
  })

  const iconSize = iconProps?.size || 24

  const height = heightVariantMap[size]

  const gradient: GradientType = (() => {
    if (disabled) return [colors.white, colors.white]
    if (danger)
      return [colors.red[50], colors.red10, colors.white75, colors.white]
    if (success) return [colors.green[100], colors.white75, colors.white]
    if (stronger)
      return [colors.gray[100], colors.gray[75], colors.gray[50], colors.white]
    return [colors.gray[50], colors.gray[50], colors.white, colors.white]
  })()

  const pressed: GradientType = (() => {
    if (danger) return [colors.red[100], colors.red10, colors.white]
    if (success) return [colors.green[100], colors.green[100], colors.white]
    if (stronger) return [colors.gray[200], colors.gray[100], colors.gray[50]]
    return [colors.gray[100], colors.gray[75], colors.white]
  })()

  const colorTitle = (() => {
    if (danger) return colors.red[600]
    if (success) return colors.green[600]
    if (disabled) return colors.neutral[300]
    return color || colors.text
  })()

  const colorDesc = (() => {
    if (danger) return hex(colors.red[600], 0.75)
    if (success) return hex(colors.green[600], 0.75)
    if (disabled) return colors.neutral[200]
    return hex(color || colors.text, 0.6)
  })()

  const colorIcon = colorTitle

  return (
    <Pressable
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      className={cn('relative inset-0', !onPress && 'pointer-events-none')}
      disabled={disabled}
    >
      <Gradient start={[0, 0.5]} end={[1, 0.5]} colors={gradient} />
      <Gradient
        start={[0, 0.5]}
        end={[1, 0.5]}
        colors={pressed}
        style={stylePressed}
      />
      {/* <Animated.View
        className="pointer-events-none absolute left-0 top-0 h-full w-full"
        style={[stylePressed, { backgroundColor: backgroundPressed }]}
      /> */}
      <Animated.View
        className={cn(
          'flex flex-row items-center justify-between gap-8 px-8',
          'border-t border-black/10',
          (stronger || danger) && 'border-black/10',
          first && 'border-black/0',
          className
        )}
        style={{ height, borderTopColor: danger ? colors.red[200] : undefined }}
      >
        <View
          className={cn(
            'flex flex-1 flex-row items-center gap-8',
            classNameInner
          )}
        >
          {icon ? (
            <IconBadge
              {...iconProps}
              name={icon}
              size={iconSize}
              color={colorIcon}
            />
          ) : (
            <View className={sIconContainer}>
              {iconComponent || (
                <View style={{ width: iconSize, height: iconSize }} />
              )}
            </View>
          )}
          <View
            className={cn(
              'flex flex-1',
              Platform.select({
                default: 'flex-row items-center justify-start gap-4', // 'flex-col items-start justify-center gap-1',
                web: 'flex-row items-center justify-start gap-4',
              })
            )}
            style={{ minHeight: Platform.select({ default: 0, web: 45 }) }}
          >
            <View className="flex flex-row items-center gap-2">
              {title && (
                <Typography
                  color={colorTitle}
                  numberOfLines={1}
                  {...titleVariantMap[size]}
                >
                  {title}
                </Typography>
              )}
              {titleComponent}
            </View>
            {desc && (
              <Typography
                variant={Platform.select({ default: 'MD', web: 'MD' })}
                className={cn('pr-16', classNameDesc)}
                color={colorDesc}
                numberOfLines={1}
              >
                {desc}
              </Typography>
            )}
          </View>
        </View>
        {children}
      </Animated.View>
    </Pressable>
  )
}
